import { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { CustomFieldFilter } from '~/components/Filters/CustomFieldFilter/CustomFieldFilter';
import { SingleFilterContainer } from '~/components/Filters/SingleFilters/SingleFilterContainer';
import { type SingleFilterProps } from '~/components/Filters/types';
import { type CustomFieldFilterType } from '~/components/FiltersMenu/types';
import {
  clearCustomFieldFilterValuesAction,
  setCustomFieldFilterLogicAction,
  setCustomFieldFilterValuesAction,
} from '~/store/filters/actions';
import {
  customFieldFilterLogicSelector,
  customFieldFilterValuesSelector,
  hasCustomFieldFilterValuesSelectedSelector,
} from '~/store/filters/selectors';
import { type FilterLogicType } from '~/store/filters/types';
import { useGetPrivateCustomFieldOptions } from '~/swr-hooks/customFields/useGetPrivateCustomFieldOptions';
import { selectableCustomFields } from '~/swr-hooks/customFields/utils';
import { useAirSelector } from '~/utils/ReduxUtils';
import { type CustomFieldValueFilterBase } from './utils';

export type SingleCustomFieldFilterProps = {
  customField: CustomFieldFilterType;
} & SingleFilterProps;

export const SingleCustomFieldFilter = memo(({ customField, onApplyClick }: SingleCustomFieldFilterProps) => {
  const {
    id: customFieldId,
    type: { title: customFieldTypeName },
  } = customField;

  const { data } = useGetPrivateCustomFieldOptions({
    customFieldId,
    enabled: selectableCustomFields.includes(customFieldTypeName),
  });

  const hasValues = useAirSelector((store) => hasCustomFieldFilterValuesSelectedSelector(store, customFieldId));

  const dispatch = useDispatch();

  const clearValues = useCallback(() => {
    dispatch(clearCustomFieldFilterValuesAction({ customFieldId }));
  }, [customFieldId, dispatch]);

  const selectedLogic = useAirSelector((state) => customFieldFilterLogicSelector(state, customFieldId));
  const selectedValues = useAirSelector((state) => customFieldFilterValuesSelector(state, customFieldId));

  const onLogicChange = useCallback(
    (logic: FilterLogicType) => dispatch(setCustomFieldFilterLogicAction({ customFieldId, logic })),
    [dispatch, customFieldId],
  );

  const onSelectedValuesChanged = useCallback(
    (values: CustomFieldValueFilterBase[]) => {
      dispatch(setCustomFieldFilterValuesAction({ customFieldId, values }));
      onApplyClick();
    },
    [dispatch, customFieldId, onApplyClick],
  );

  return (
    <SingleFilterContainer
      hasValues={hasValues}
      currentFilter="customField"
      currentCustomField={customField}
      onClearClick={clearValues}
      onApplyClick={onApplyClick}
    >
      <CustomFieldFilter
        values={data?.data ?? []}
        onLogicChange={onLogicChange}
        selectedLogic={selectedLogic}
        selectedValues={selectedValues}
        onSelectedValuesChange={onSelectedValuesChanged}
      />
    </SingleFilterContainer>
  );
});

SingleCustomFieldFilter.displayName = 'SingleCustomFieldFilter';
